import React, { Component } from 'react'
import PropTypes from 'prop-types'
import qs from 'qs'
import { isNil, isUndefined, isEmpty, get, cloneDeep, some, find, snakeCase, merge } from 'lodash'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  Button, FormField, Select, SelectContent, SelectTrigger, SelectValue, Switch
} from '@ideon-api/ideon-ui'
import withRouter from '../../../lib/withRouter'
import ZipFipsComponent from './ZipFipsComponent'
import PlansComponent from './PlansComponent'
import getZipCounties from '../../../actions/CarrierVerification/getZipCounties'
import FeedbackComponent from './FeedbackComponent'
import { IdeonInput, IdeonSelect } from '../../common/ideon/fields'
import { toToggleProps } from '../../common/ideon/formAdapters'
import {
  getAllAudienceCensuses,
  setAudience,
  setAudienceByCensusId,
  getPlanSearches,
  getCensusPlanSearches,
  resetCensusPlanSearches,
  createScenarioFeedback
} from '../../../actions/CarrierVerification'

import groupActions from '../../../actions/VericredGroups'
import quoteActions from '../../../actions/VericredQuotes'
import quoteRatesActions from '../../../actions/VericredQuoteRates'
import groupMembersActions from '../../../actions/VericredGroupMembers'
import limitingFactorsActions from '../../../actions/VericredLimitingFactors'
import platformActions from '../../../actions/Platforms'
import { platformSelectParams } from '../../../lib/platformSelectParams'
import ScenarioInfo from './ScenarioInfo'

export class ScenariosContainer extends Component {
  static propTypes = {
    location: PropTypes.object,
    getAllAudienceCensuses: PropTypes.func.isRequired,
    setAudience: PropTypes.func.isRequired,
    getCensusPlanSearches: PropTypes.func.isRequired,
    audience: PropTypes.string,
    getZipCounties: PropTypes.func.isRequired,
    zipCounties: PropTypes.array,
    plans: PropTypes.array,
    censuses: PropTypes.array,
    plansByFamily: PropTypes.object,
    needFamilies: PropTypes.bool,
    requestInProgress: PropTypes.bool,
    resourceErrors: PropTypes.string,
    generalError: PropTypes.string,
    plansResourceErrors: PropTypes.string,
    plansGeneralError: PropTypes.string,
    resetCensusPlanSearches: PropTypes.func.isRequired,
    created: PropTypes.bool,
    setAudienceByCensusId: PropTypes.func,
    createScenarioFeedback: PropTypes.func,
    getPlanSearches: PropTypes.func,
    censusesInProgress: PropTypes.bool,
    configuration: PropTypes.object,
    scenarioActionTaken: PropTypes.string,
    zipFipsRequestInProgress: PropTypes.bool,
    groupMembersCreate: PropTypes.func.isRequired,
    groupMembersReset: PropTypes.func.isRequired,
    groupCreate: PropTypes.func.isRequired,
    groupReset: PropTypes.func.isRequired,
    group: PropTypes.object,
    quoteCreate: PropTypes.func.isRequired,
    quoteCreateReset: PropTypes.func.isRequired,
    quoteShow: PropTypes.func.isRequired,
    quoteShowReset: PropTypes.func.isRequired,
    quoteRatesCreate: PropTypes.func.isRequired,
    quoteRatesReset: PropTypes.func.isRequired,
    limitingFactorsCreate: PropTypes.func.isRequired,
    limitingFactorsCreateReset: PropTypes.func.isRequired,
    platformsIndex: PropTypes.func.isRequired,
    quote: PropTypes.object,
    quoteAttributes: PropTypes.object,
    quoteRates: PropTypes.object,
    groupMembers: PropTypes.object,
    history: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    const values = this.stateValuesFromQS

    if (values.census_id) {
      this.props.setAudienceByCensusId(values.census_id)
    }
    this.state = {
      values,
      pollTimeout: null
    }
  }

  componentDidMount() {
    this.updateEntitledPlatforms()
    this.updateCensusesIfNeeded()
  }

  componentDidUpdate(prevProps) {
    this.adoptCensusIdFromQueryString(prevProps)
    this.updateCensusesIfNeeded(prevProps)
  }

  componentWillUnmount() {
    this.props.setAudience(null)
    this.resetGroupQuoteRequests()
    clearTimeout(this.state.pollTimeout)
    this.setState({ pollTimeout: null })
  }

  get queryStrings() {
    return qs.parse(this.props.location.search, { ignoreQueryPrefix: true })
  }

  get stateValuesFromQS() {
    const parsedValues = this.parseStateFromQueryStrings(this.queryStrings)

    return parsedValues
  }

  get selectedCensus() {
    const censusId = this.state.values.census_id

    if (!censusId) {
      return null
    }

    return find(this.props.censuses, (p) => p.id === censusId)
  }

  get submitButton() {
    const anyRequestInProgress = this.allRequestInProgress
    const sanitizedValues = cloneDeep(this.sanitizedStateValues)
    delete sanitizedValues.message
    delete sanitizedValues.commission_amount
    delete sanitizedValues.quoting_platform

    const disableSubmit = some(sanitizedValues, (value) => isNil(value) || isEmpty(value.toString()))

    return (
      <Button type="submit" variant="filled" disabled={anyRequestInProgress || disableSubmit}>
        {anyRequestInProgress ? 'Running…' : 'Run Scenario'}
      </Button>
    )
  }

  // Three census states, not two: censusesByAudience is nil both before a
  // market is chosen and while the request is in flight, and those must look different (PD-30709).
  get censusField() {
    const { audience, censuses, censusesInProgress } = this.props

    // Both pre-selection states keep the real control in place and differ only in
    // what it says. A skeleton swapped in for the select made the field look like
    // it had vanished, which reads as breakage rather than as waiting.
    if (!audience) return this.pendingCensusField('Select a market first')
    if (censusesInProgress) return this.pendingCensusField('Loading censuses…', true)

    // Not gated on isNil(censuses) too -- a RECEIVE_CENSUS_ERROR clears getInProgress without
    // populating censuses, which would spin the skeleton forever on exactly the failure the reducer fix was for.
    return (
      <IdeonSelect
        name="census_id"
        label="Censuses"
        options={(censuses || []).map((census) => ({ text: census.name, value: census.id }))}
        value={this.state.values.census_id}
        handleChange={this.handleChange}
        cast={Number}
      />
    )
  }

  // A quiet section eyebrow -- smaller and lighter than a field label
  // (text-sm/500), differentiated by weight/case/tracking rather than size
  // alone, so the five field groups read as grouping rather than mixing in
  // with the labels inside them.
  get legendClassName() {
    return 'text-xs font-semibold uppercase tracking-wide text-ideon-gray-70 dark:text-ideon-gray-60 mb-2'
  }

  // Group and Rating apply only once an audience has actually been chosen
  // AND it is not Individual -- a bare !== 'Individual' check is also true
  // for null/undefined/'', so these fields used to render on a fresh page
  // load before any market was picked (PD-30709).
  get audienceRequiresGroupFields() {
    const { audience } = this.sanitizedStateValues
    return !isEmpty(audience) && audience !== 'Individual'
  }

  get groupComponents() {
    if (!this.audienceRequiresGroupFields) { return null }

    return (
      <fieldset className="mt-8">
        <legend className={this.legendClassName}>Group</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <IdeonInput
            name="sic_code"
            label="SIC Code"
            handleChange={this.handleChange}
            value={this.state.values.sic_code}
          />
          <IdeonInput
            name="number_of_employees"
            label="Eligible Members"
            type="number"
            step="1"
            max={100}
            min={0}
            handleChange={this.handleChange}
            value={this.state.values.number_of_employees}
          />
          <IdeonInput
            name="participation_percentage"
            label="Participation Percentage"
            type="number"
            step="1"
            max={100}
            min={0}
            handleChange={this.handleChange}
            value={this.state.values.participation_percentage}
          />
          <IdeonInput
            name="contribution_percentage"
            label="Contribution Percentage"
            type="number"
            step="1"
            max={100}
            min={0}
            handleChange={this.handleChange}
            value={this.state.values.contribution_percentage}
          />
        </div>
      </fieldset>
    )
  }

  get ratingComponents() {
    if (!this.audienceRequiresGroupFields) { return null }

    return (
      <fieldset className="mt-8">
        <legend className={this.legendClassName}>Rating</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <IdeonSelect
            name="rating_method"
            label="Rating Method"
            options={this.ratingMethodOptions}
            handleChange={this.handleChange}
            value={this.state.values.rating_method}
            disabled={this.disableRatingMethod}
          />
          {/* Toggle goes last: a switch between two boxed inputs reads as a gap in the row. */}
          {this.commissionAmountField}
          {this.toggleField('voluntary', 'Voluntary', this.state.values.voluntary)}
        </div>
      </fieldset>
    )
  }

  get commissionAmountField() {
    if (this.sanitizedStateValues.audience !== 'Level Funded') { return null }

    return (
      <IdeonInput
        name="commission_amount"
        label="Commission Amount"
        type="number"
        step="1"
        min={0}
        handleChange={this.handleChange}
        value={this.state.values.commission_amount}
      />
    )
  }

  get privilegedPlatformsComponents() {
    if (this.disableCustomPlatformRequests()) { return null }

    return (
      <IdeonSelect
        name="quoting_platform"
        label="Quoting Platform"
        options={this.state.entitledPlatforms}
        handleChange={this.handleChange}
        value={this.state.values.quoting_platform}
      />
    )
  }

  get disableRatingMethod() {
    return ['life', 'disability', 'critical_illness'].includes(this.sanitizedStateValues.product_line)
  }

  get ratingMethodOptions() {
    return [
      { text: 'Age Banded', value: 'age_banded' },
      { text: '4 Tier Composite', value: '4_tier_composite' },
      { text: '3 Tier Composite', value: '3_tier_composite' },
      { text: '2 Tier Composite', value: '2_tier_composite' }
    ]
  }

  get sanitizedStateValues() {
    const values = cloneDeep(this.state.values)
    return this.sanitizedValues(values, this.props.audience, this.selectedCensus)
  }

  // plansResourceErrors/plansGeneralError are the `carrierVerification.plans`
  // slice -- where getPlanSearches records a failure. It was missing from both
  // lists (PD-30615), which is how the most literal "the plan search failed"
  // path went unnoticed: submit fires getPlanSearches AND the census/group
  // request against different payloads, so getPlanSearches can fail alone.
  // When it did, this returned undefined, PlansComponent fell through to its
  // `!plans` branch and rendered nothing at all, and Approve stayed live over
  // an empty list.
  get allResourceErrors() {
    return find([this.props.plansResourceErrors,
      this.props.resourceErrors,
      this.props.group.resourceErrors,
      this.props.quote.resourceErrors,
      this.props.quoteRates.resourceErrors,
      this.props.groupMembers.resourceErrors], (p) => !isUndefined(p))
  }

  get allGeneralErrors() {
    return find([this.props.plansGeneralError,
      this.props.generalError,
      this.props.group.generalError,
      this.props.quote.generalError,
      this.props.quoteRates.generalError,
      this.props.groupMembers.generalError], (p) => !isUndefined(p))
  }

  // The same two sources PlansComponent renders its error from, so the buttons
  // and the error banner can never disagree about whether the search worked.
  get searchFailed() {
    return !isUndefined(this.allGeneralErrors) || !isUndefined(this.allResourceErrors)
  }

  get allRequestInProgress() {
    return (get(this.props, 'requestInProgress', false)
      || get(this.props, 'plansRequestInProgress', false)
      || get(this.props, 'group.requestInProgress', false)
      || get(this.props, 'quote.requestInProgress', false)
      || get(this.props, 'quoteRates.requestInProgress', false)
      || get(this.props, 'groupMembers.requestInProgress', false)
      || this.state.pollTimeout != null)
  }

  get quoteComplete() {
    return get(this.props.quoteAttributes, 'status') === 'complete'
  }

  // The census select before it can be used: same shape and position as the real
  // one, so nothing shifts when the options arrive. aria-busy marks the wait.
  pendingCensusField = (placeholder, busy = false) => (
    <FormField label="Censuses" htmlFor="census_id">
      <Select disabled>
        <SelectTrigger id="census_id" aria-busy={busy || undefined}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent />
      </Select>
    </FormField>
  )

  // IdeonSwitch puts its label beside the control, which is right for its
  // other consumer (IssuerStatusEdit) but hugs the top of a grid cell here,
  // out of line with every input's label-above/control-below band. Built
  // straight on toToggleProps instead, in the same h-10 band every other
  // field in this grid uses.
  toggleField = (name, label, value) => (
    <FormField label={label} htmlFor={name}>
      <div className="flex h-10 items-center">
        <Switch id={name} {...toToggleProps({ name, value, handleChange: this.handleChange }, { cast: String })} />
      </div>
    </FormField>
  )

  disableCustomPlatformRequests = () => {
    return isEmpty(this.state.entitledPlatforms)
      || ['medical', ''].includes(this.sanitizedStateValues.product_line)
  }

  transformPlatformRecords = (platforms) => {
    return platforms.map((platform) => {
      return { text: platform.name, value: platform.name }
    })
  }

  handleChange = (field, value) => {
    this.setState((prevState) => {
      const newState = prevState.values

      if (isNil(value)) {
        delete newState[field]
      } else {
        newState[field] = value
      }

      return { values: newState }
    })

    if (field !== 'message') {
      this.updateHistory(field, value)
    }
  }

  handleAudienceChange = (field, value) => {
    const previous = this.props.audience
    // Radix emits an empty value when its own value has no matching item, which
    // happens here because audienceOptions is [] until a product line is picked.
    // A user cannot choose an empty market, so this is never a real selection --
    // and acting on it wiped both the audience and a deep link's census_id.
    if (isEmpty(value)) return
    if (value === previous) return

    // Only a genuine switch BETWEEN markets invalidates the chosen census.
    // Clearing unconditionally also wiped a deep link's census_id while the
    // audience was still resolving from it, which is a circular wipe (PD-30709).
    if (!isEmpty(previous)) {
      this.handleChange('census_id', '')
      // The previous audience's results and errors do not describe the new one.
      this.resetRequests()
    }

    this.props.setAudience(value)
  }

  sanitizedValues = (values, audience, census) => {
    const zipFips = this.zipFipsValuesFromString(values.zip_fips)
    const market = audience ? audience.toLowerCase().split(' ').join('_') : ''
    const effectiveDateString = values.effective_date
    const censusName = census ? census.name : ''
    let ratingMethod = values.rating_method

    if (['critical_illness'].includes(snakeCase(values.product_line))) {
      ratingMethod = '4_tier_composite'
    }

    return {
      zip_code: zipFips.zip_code,
      fips_code: zipFips.fips_code,
      market,
      effective_date: values.effective_date,
      audience,
      census_name: censusName,
      census_id: values.census_id,
      effectiveDateString,
      message: values.message,
      product_line: snakeCase(values.product_line),
      sic_code: values.sic_code,
      number_of_employees: values.number_of_employees,
      voluntary: values.voluntary,
      contribution_percentage: values.contribution_percentage,
      participation_percentage: values.participation_percentage,
      rating_method: ratingMethod,
      commission_amount: values.commission_amount,
      quoting_platform: values.quoting_platform,
      quote_via_carrier_api: values.quote_via_carrier_api,
      npn: values.npn
    }
  }

  generateGroupQuote = async () => {
    this.resetGroupQuoteRequests()

    await this.createGroup()

    await this.createGroupMembers()

    await this.createQuote()

    if (this.props.quote.created) {
      this.waitQuoteComplete()
    }
  }

  createGroup = async () => {
    const values = this.sanitizedStateValues
    await this.props.groupCreate(
      {
        voluntary: values.voluntary,
        sic_code: values.sic_code,
        fips_code: values.fips_code,
        zip_code: values.zip_code,
        number_of_employees: values.number_of_employees
      }
    )
  }

  createGroupMembers = async () => {
    if (!this.props.group.created) return

    const values = this.sanitizedStateValues
    await this.props.groupMembersCreate(
      {
        group_id: this.props.group.id,
        location_id: this.props.group.responseAttributes.location_id,
        zip_code: values.zip_code,
        fips_code: values.fips_code,
        census_id: values.census_id,
        effective_date: values.effectiveDateString
      }
    )
  }

  createQuote = async () => {
    if (!this.props.group.created) return

    const values = this.sanitizedStateValues
    let params = {
      group_id: this.props.group.id,
      contribution_percentage: values.contribution_percentage,
      effective_date: values.effectiveDateString,
      participation_percentage: values.participation_percentage,
      product_line: snakeCase(values.product_line),
      rating_method: values.rating_method,
      voluntary: values.voluntary,
      market: values.market,
      quoting_platform: values.quoting_platform,
      quote_via_carrier_api: values.quote_via_carrier_api,
      npn: values.npn
    }

    if (values.market === 'level_funded' && !isNil(values.commission_amount)) {
      params = merge(params, { commission_amount: values.commission_amount })
    }

    await this.props.quoteCreate(params)
  }

  waitQuoteComplete = () => {
    if (this.quoteComplete) {
      this.fetchQuoteRates()
      this.fetchLimitingFactors()
      this.setState({ pollTimeout: null })
    } else {
      const pollTimeout = setTimeout(
        async () => {
          await this.fetchQuote()
          this.waitQuoteComplete()
        }, 3000
      )
      this.setState({ pollTimeout })
    }
  }

  fetchQuote = async () => {
    await this.props.quoteShow(this.props.quote.id)
  }

  fetchQuoteRates = () => {
    this.props.quoteRatesCreate({ quote_id: this.props.quote.id })
  }

  fetchLimitingFactors = () => {
    this.props.limitingFactorsCreate({ quote_id: this.props.quote.id })
  }

  // Every per-run slice, regardless of which branch this run will take.
  resetRequests = () => {
    this.props.resetCensusPlanSearches()
    this.resetGroupQuoteRequests()
  }

  resetGroupQuoteRequests = () => {
    this.props.limitingFactorsCreateReset()
    this.props.quoteRatesReset()
    this.props.quoteCreateReset()
    this.props.quoteShowReset()
    this.props.groupMembersReset()
    this.props.groupReset()
  }

  submit = (e) => {
    e.preventDefault()
    const values = this.sanitizedStateValues
    // Clear every slice this run will write to, before it writes (PD-30615).
    // Each slice only cleared itself on its own request, and a run does not
    // touch every slice: the Individual branch skipped resetGroupQuoteRequests
    // and the group branch never dispatched REQUEST_CENSUS_PLANS. So an error
    // from a failed run in one audience survived into a SUCCESSFUL run in the
    // other -- the plan list showed the failure state and Approve/Reject stayed
    // hidden, until a full page reload.
    this.resetRequests()
    this.props.createScenarioFeedback()
    this.props.getPlanSearches(
      values.product_line,
      values.zip_code,
      values.fips_code,
      values.market,
      values.effectiveDateString,
      values.quoting_platform
    )

    if (this.props.audience === 'Individual') {
      const sanitizedFamilies = this.sanitizedFamilies(this.selectedCensus.families)
      this.props.getCensusPlanSearches(
        values.product_line,
        values.zip_code,
        values.fips_code,
        values.market,
        values.effectiveDateString,
        sanitizedFamilies
      )
    } else {
      this.generateGroupQuote()
    }
  }

  handleScenarioFeedback = (approved) => {
    const values = this.sanitizedStateValues
    values.approved = approved
    this.props.createScenarioFeedback(values)
  }

  zipFipsFromQueryString(queryStrings) {
    const zipFips = get(queryStrings, 'zip_fips', '')
    const zipCode = this.zipFipsValuesFromString(zipFips).zip_code

    return !isUndefined(zipCode) && zipCode.length === 5 ? zipFips : undefined
  }

  parseStateFromQueryStrings(queryStrings) {
    return {
      zip_fips: this.zipFipsFromQueryString(queryStrings),
      census_id: global.parseInt(get(queryStrings, 'census_id', null)) || undefined,
      effective_date: get(queryStrings, 'effective_date', undefined),
      product_line: get(queryStrings, 'product_line', undefined),
      sic_code: get(queryStrings, 'sic_code', '0100'),
      number_of_employees: get(queryStrings, 'number_of_employees', '30'),
      voluntary: get(queryStrings, 'voluntary', 'true'),
      participation_percentage: get(queryStrings, 'participation_percentage', '100'),
      contribution_percentage: get(queryStrings, 'contribution_percentage', '100'),
      rating_method: get(queryStrings, 'rating_method', 'age_banded'),
      commission_amount: get(queryStrings, 'commission_amount', null),
      quoting_platform: get(queryStrings, 'quoting_platform', undefined),
      quote_via_carrier_api: get(queryStrings, 'quote_via_carrier_api', 'false'),
      npn: get(queryStrings, 'npn', '123456')
    }
  }

  // The constructor reads census_id once, so a deep link that arrives after
  // mount -- the router settling a redirect, say -- never resolved its audience
  // and left the census select stuck on "Select a market first" (PD-30709).
  adoptCensusIdFromQueryString(prevProps) {
    if (isNil(prevProps) || prevProps.location.search === this.props.location.search) return

    const censusId = this.stateValuesFromQS.census_id
    if (!censusId || censusId === this.state.values.census_id) return

    this.setState((prev) => ({ values: { ...prev.values, census_id: censusId } }))
    this.props.setAudienceByCensusId(censusId)
  }

  updateCensusesIfNeeded(prevProps) {
    if ((isNil(prevProps) && this.state.values.census_id)
      || (!isNil(prevProps) && this.props.audience && prevProps.audience !== this.props.audience)) {
      this.props.getAllAudienceCensuses(this.props.audience)
    }
  }

  updateEntitledPlatforms() {
    this.props.platformsIndex(platformSelectParams).then((response) => {
      this.setState({ entitledPlatforms: this.transformPlatformRecords(response.records) })
    })
  }

  updateHistory(key, value) {
    const search = this.queryStrings
    if (isNil(value)) {
      delete search[key]
      if (isEmpty(search)) {
        this.props.history('/scenarios')
      } else {
        this.updateRouterHistory(search)
      }
    } else {
      search[key] = value
      this.updateRouterHistory(search)
    }
  }

  updateRouterHistory(search) {
    const queryStrings = qs.stringify(search)
    this.props.history(`/scenarios?${queryStrings}`)
  }

  audienceOptions() {
    const configuration = this.props.configuration
    const productLine = this.state.values.product_line
    const audiences = get(configuration.product_line_audiences, productLine, [])

    return audiences.map((audience) => ({
      text: audience,
      value: audience
    }))
  }

  productLineOptions(productLines) {
    return productLines.map((productLine) => ({
      text: productLine,
      value: productLine
    }))
  }

  zipFipsValuesFromString(originalZipfips) {
    let zipfips = originalZipfips
    zipfips = zipfips ? zipfips.split('-') : []
    const zipCode = zipfips.length > 1 ? zipfips[0] : undefined
    const fipsCode = zipfips.length > 1 ? zipfips[1] : undefined
    return { zip_code: zipCode, fips_code: fipsCode }
  }

  sanitizedFamilies(families) {
    return families.map((family) => {
      const applicants = family.members
        .map((m) => (
          {
            age: m.age,
            smoker: m.tobacco,
            child: m.member_type === 'child',
            gender: m.gender[0].toUpperCase()
          }
        ))

      return (
        {
          id: family.id,
          name: family.name,
          household_income: family.household_income,
          household_size: family.household_size,
          applicants
        }
      )
    })
  }

  render() {
    const {
      configuration,
      scenarioActionTaken,
      zipCounties,
      zipFipsRequestInProgress,
      plans,
      plansByFamily,
      quoteRates,
      created,
      requestInProgress
    } = this.props

    let needFamilies = this.props.needFamilies
    const plansPrices = this.props.audience === 'Individual' ? plansByFamily : quoteRates.responseAttributes
    if (this.props.audience !== 'Individual') {
      needFamilies = !plansPrices
    }

    const productLine = !isUndefined(this.state.values.product_line)
      ? this.state.values.product_line.toLowerCase().replace(/ /, '_')
      : ''
    const audienceOptions = this.audienceOptions()
    const productLineOptions = this.productLineOptions(configuration.product_lines)
    const displayMonthlyPremium = !['life',
      'disability',
      'hospital_indemnity',
      'critical_illness'].includes(productLine)
    const displayContractCode = !['life',
      'disability',
      'hospital_indemnity',
      'critical_illness'].includes(productLine)
    const displayBenefitsTab = !['life', 'disability'].includes(productLine)
    const usePlanRateComponent = ['life', 'disability', 'hospital_indemnity', 'critical_illness'].includes(productLine)
    const sortPlansByQuoteRate = ['life', 'disability', 'accident'].includes(productLine)
    /* eslint-disable react/jsx-no-bind */
    const showQuote = this.props.audience !== 'Individual'

    return (
      <div className="content">
        <form className="ideon-ui-scope" onSubmit={this.submit}>
          <fieldset>
            <legend className={this.legendClassName}>Coverage</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <IdeonSelect
                name="product_line"
                label="Line Of Coverage"
                value={this.state.values.product_line}
                handleChange={this.handleChange}
                options={productLineOptions}
              />
              <IdeonSelect
                name="audience"
                label="Market"
                options={audienceOptions}
                handleChange={this.handleAudienceChange}
                value={this.props.audience}
              />
              <IdeonInput
                name="effective_date"
                label="Effective Date"
                type="date"
                value={this.state.values.effective_date}
                handleChange={this.handleChange}
              />
            </div>
          </fieldset>

          <fieldset className="mt-8">
            <legend className={this.legendClassName}>Location and Census</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ZipFipsComponent
                value={this.state.values.zip_fips}
                handleChange={this.handleChange}
                requestInProgress={zipFipsRequestInProgress}
                zipCounties={zipCounties}
                getZipCounties={this.props.getZipCounties}
              />
              {this.censusField}
            </div>
          </fieldset>

          {this.groupComponents}
          {this.ratingComponents}

          <fieldset className="mt-8">
            <legend className={this.legendClassName}>Request Options</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <IdeonInput
                name="npn"
                label="NPN"
                value={this.state.values.npn}
                handleChange={this.handleChange}
              />
              {this.toggleField(
                'quote_via_carrier_api', 'Quote via Carrier API', this.state.values.quote_via_carrier_api
              )}
              {this.privilegedPlatformsComponents}
            </div>
          </fieldset>

          <div className="mt-8">
            {this.submitButton}
          </div>
        </form>

        {((!requestInProgress && created) || quoteRates.created)
          && (
            <ScenarioInfo
              quoteId={this.props.quote.id}
              groupId={this.props.group.id}
              showQuote={showQuote}
            />
          )}

        <PlansComponent
          census={this.selectedCensus}
          needFamilies={needFamilies}
          plans={plans}
          audience={this.props.audience}
          plansByFamily={plansPrices}
          displayMonthlyPremium={displayMonthlyPremium}
          displayContractCode={displayContractCode}
          displayBenefitsTab={displayBenefitsTab}
          usePlanRateComponent={usePlanRateComponent}
          sortPlansByQuoteRate={sortPlansByQuoteRate}
          requestInProgress={this.props.requestInProgress}
          resourceErrors={this.allResourceErrors}
          generalError={this.allGeneralErrors}
        />

        <FeedbackComponent
          handleApproveScenario={this.handleScenarioFeedback.bind(this, true)}
          handleRejectScenario={this.handleScenarioFeedback.bind(this, false)}
          scenarioActionTaken={scenarioActionTaken}
          rejectionMessage={this.state.values.message}
          handleChange={this.handleChange}
          needFamilies={needFamilies}
          searchFailed={this.searchFailed}
        />
      </div>
    )
    /* eslint-enable react/jsx-no-bind */
  }
}

const mapStateToProps = (state) => {
  const audience = state.carrierVerification.scenariosAudience.audience
  const censuses = get(state.carrierVerification.allCensuses.censusesByAudience, audience)
  const group = state.groups.create
  const quote = state.quotes.create
  const quoteAttributes = state.quotes.show.attributes || state.quotes.create.responseAttributes
  const quoteRates = state.quoteRates.create
  const groupMembers = state.groupMembers.create
  const limitingFactors = state.limitingFactors.create

  return {
    group,
    quote,
    quoteAttributes,
    quoteRates,
    groupMembers,
    limitingFactors,
    configuration: state.login.session.configuration.censuses,
    entitlements: state.login.session.configuration.entitlements,
    censusesInProgress: state.carrierVerification.allCensuses.getInProgress,
    censuses,
    audience,
    scenarioActionTaken: state.carrierVerification.scenarios.actionTaken,
    plansRequestInProgress: state.carrierVerification.plans.getInProgress,
    zipCounties: state.carrierVerification.zipCounties.zipCounties,
    zipFipsRequestInProgress: state.carrierVerification.zipCounties.getInProgress,
    plans: state.carrierVerification.plans.plans,
    plansByFamily: state.carrierVerification.censusPlans.plansByFamily,
    requestInProgress: state.carrierVerification.plans.getInProgress,
    resourceErrors: state.carrierVerification.censusPlans.resourceErrors,
    generalError: state.carrierVerification.censusPlans.generalError,
    plansResourceErrors: state.carrierVerification.plans.resourceErrors,
    plansGeneralError: state.carrierVerification.plans.generalError,
    created: state.carrierVerification.censusPlans.created,
    needFamilies: (
      isUndefined(get(state, 'carrierVerification.censusPlans.outstandingFamilyIds'))
      || !isEmpty(get(state, 'carrierVerification.censusPlans.outstandingFamilyIds'))
    )
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getPlanSearches,
  getAllAudienceCensuses,
  setAudience,
  setAudienceByCensusId,
  getCensusPlanSearches,
  resetCensusPlanSearches,
  createScenarioFeedback,
  getZipCounties,
  groupCreate: groupActions.create.main,
  groupMembersCreate: groupMembersActions.create.main,
  quoteCreate: quoteActions.create.main,
  quoteShow: quoteActions.show.main,
  quoteRatesCreate: quoteRatesActions.create.main,
  limitingFactorsCreate: limitingFactorsActions.create.main,
  quoteRatesReset: quoteRatesActions.create.reset,
  quoteCreateReset: quoteActions.create.reset,
  quoteShowReset: quoteActions.show.reset,
  limitingFactorsCreateReset: limitingFactorsActions.create.reset,
  groupMembersReset: groupMembersActions.create.reset,
  groupReset: groupActions.create.reset,
  platformsIndex: platformActions.index.main
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ScenariosContainer))
